import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const choicesOptions = [
  {
    value: 'values_aligned',
    label: 'Yes, I made choices aligned with my values',
    icon: '✨'
  },
  {
    value: 'practiced',
    label: 'Sometimes - I practiced even when it was hard',
    icon: '💪'
  },
  {
    value: 'learned',
    label: 'Not today, but I learned something',
    icon: '🌱'
  }
];

// Day-specific evening reflections
const getDayContent = () => {
  const dayOfWeek = new Date().getDay();
  const dayContent = {
    0: { // Sunday
      theme: "Rest Complete",
      prompt: "As Sunday ends, how did you honor your need for rest today?",
      closingAffirmation: "Rest is part of recovery. You showed wisdom today.",
      gratitudePrompt: "What restful moment are you grateful for?"
    },
    1: { // Monday
      theme: "Week Begun",
      prompt: "How did your Monday go? Did you honor your morning intention?",
      closingAffirmation: "You showed up today. That's what matters.",
      gratitudePrompt: "What new beginning are you grateful for?"
    },
    2: { // Tuesday
      theme: "Momentum Building",
      prompt: "Tuesday's done. How did you build on yesterday's progress?",
      closingAffirmation: "Consistency over perfection. You're building.",
      gratitudePrompt: "What progress are you grateful for?"
    },
    3: { // Wednesday
      theme: "Midweek Milestone",
      prompt: "You've made it halfway through the week. How are you feeling?",
      closingAffirmation: "Halfway there. You're proving you can do this.",
      gratitudePrompt: "What strength have you shown this week?"
    },
    4: { // Thursday
      theme: "Gratitude Day",
      prompt: "Thursday is for deep gratitude. What unexpected good happened today?",
      closingAffirmation: "Gratitude changes your brain. You're rewiring for joy.",
      gratitudePrompt: "What surprised you with goodness today?"
    },
    5: { // Friday
      theme: "Week Complete",
      prompt: "Friday's done. How did you handle any end-of-week challenges?",
      closingAffirmation: "You made it through the week. Celebrate that.",
      gratitudePrompt: "What accomplishment are you grateful for this week?"
    },
    6: { // Saturday
      theme: "Freedom Used Well",
      prompt: "How did you use your Saturday freedom? Did it serve who you're becoming?",
      closingAffirmation: "Free time well-spent is a gift to yourself.",
      gratitudePrompt: "What meaningful experience are you grateful for?"
    }
  };
  return dayContent[dayOfWeek];
};

export default function EveningReflection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dayContent = getDayContent();
  const [formData, setFormData] = useState({
    energy_level: 5,
    choices_reflection: '',
    what_worked: '',
    gratitude: '',
    tomorrow_focus: '',
    daily_reflection: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.choices_reflection) {
      toast.error('Please share how your day went');
      return;
    }
    
    if (!formData.what_worked.trim()) {
      toast.error('Please share what worked today');
      return;
    }

    if (!formData.gratitude.trim()) {
      toast.error('Please share what you\'re grateful for');
      return;
    }

    if (!formData.tomorrow_focus.trim()) {
      toast.error('Please set your focus for tomorrow');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/check-ins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: 'evening',
          ...formData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save reflection');
      }

      toast.success('Well done. Rest well tonight.');
      navigate('/dashboard', { state: { checkInComplete: true } });
    } catch (error) {
      toast.error(error.message || 'Failed to save reflection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen landing-hero flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-[#3A4550] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#6F8C9B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F8F9FA]" data-testid="evening-reflection-title">
            Evening Reflection
          </h1>
          <p className="text-base text-[#ADB5BD] leading-relaxed">
            {dayContent.theme}
          </p>
        </div>

        {/* Daily Prompt Card */}
        <div className="bg-gradient-to-br from-[#6F8C9B]/20 to-[#8FAF9A]/20 rounded-2xl p-5 border border-[#6F8C9B]/30">
          <p className="text-center text-[#F8F9FA] font-medium">
            {dayContent.prompt}
          </p>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="bg-[#2D3640] rounded-2xl border border-[#4A5568] shadow-sm p-6 space-y-6">
          {/* Daily Reflection */}
          <div className="space-y-2">
            <Label className="text-base font-medium text-[#F8F9FA]">
              Reflect on today:
            </Label>
            <Textarea
              value={formData.daily_reflection}
              onChange={(e) => setFormData({ ...formData, daily_reflection: e.target.value })}
              placeholder="How did today go..."
              className="min-h-20 rounded-xl border-[#4A5568] focus:border-[#6F8C9B] focus:ring-[#6F8C9B]/20 text-base"
              data-testid="daily-reflection-input"
            />
          </div>

          {/* Energy Level */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-base font-medium text-[#F8F9FA]">
                How was your energy today?
              </Label>
              <span className="text-2xl font-bold text-[#6F8C9B]" data-testid="energy-value">
                {formData.energy_level}
              </span>
            </div>
            <Slider
              value={[formData.energy_level]}
              onValueChange={(value) => setFormData({ ...formData, energy_level: value[0] })}
              min={1}
              max={10}
              step={1}
              className="w-full"
              data-testid="energy-slider"
            />
            <div className="flex justify-between text-xs text-[#AEB8B5]">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>

          {/* Choices Reflection */}
          <div className="space-y-3">
            <Label className="text-base font-medium text-[#F8F9FA]">
              Did you choose differently today?
            </Label>
            <RadioGroup
              value={formData.choices_reflection}
              onValueChange={(value) => setFormData({ ...formData, choices_reflection: value })}
              className="space-y-3"
            >
              {choicesOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-start gap-4 p-4 rounded-xl border border-[#4A5568] hover:border-[#6F8C9B] hover:bg-[#3A4550]/30 cursor-pointer transition-all"
                  data-testid={`choice-option-${option.value}`}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="mt-0.5" />
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-2xl">{option.icon}</span>
                    <span className="text-base text-[#F8F9FA] leading-relaxed">
                      {option.label}
                    </span>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* What Worked */}
          <div className="space-y-2">
            <Label className="text-base font-medium text-[#F8F9FA]">
              What worked today?
            </Label>
            <Textarea
              value={formData.what_worked}
              onChange={(e) => setFormData({ ...formData, what_worked: e.target.value })}
              placeholder="Even small wins count..."
              className="min-h-24 rounded-xl border-[#4A5568] focus:border-[#6F8C9B] focus:ring-[#6F8C9B]/20 text-base"
              data-testid="what-worked-input"
            />
          </div>

          {/* Gratitude - Day Specific */}
          <div className="space-y-2">
            <Label className="text-base font-medium text-[#F8F9FA]">
              {dayContent.gratitudePrompt}
            </Label>
            <Textarea
              value={formData.gratitude}
              onChange={(e) => setFormData({ ...formData, gratitude: e.target.value })}
              placeholder="Today I&apos;m grateful for..."
              className="min-h-24 rounded-xl border-[#4A5568] focus:border-[#6F8C9B] focus:ring-[#6F8C9B]/20 text-base"
              data-testid="gratitude-input"
            />
          </div>

          {/* Tomorrow Focus */}
          <div className="space-y-2">
            <Label className="text-base font-medium text-[#F8F9FA]">
              Tomorrow I will focus on:
            </Label>
            <Input
              value={formData.tomorrow_focus}
              onChange={(e) => setFormData({ ...formData, tomorrow_focus: e.target.value })}
              placeholder="One thing to focus on"
              className="h-12 rounded-xl border-[#4A5568] focus:border-[#6F8C9B] focus:ring-[#6F8C9B]/20 text-base"
              data-testid="tomorrow-focus-input"
            />
          </div>

          {/* Closing Affirmation */}
          <div className="p-4 bg-gradient-to-br from-[#8FAF9A]/10 to-[#6F8C9B]/10 rounded-xl border border-[#8FAF9A]/30">
            <p className="text-center text-[#F8F9FA] font-medium italic">
              &quot;{dayContent.closingAffirmation}&quot;
            </p>
          </div>

          {/* Submit Button */}
          <div className="space-y-3 pt-2">
            <Button
              type="submit"
              disabled={loading}
              className="button-primary w-full rounded-full px-8 py-6 h-auto text-lg font-medium"
              data-testid="submit-reflection-btn"
            >
              {loading ? 'Saving reflection...' : 'Complete Reflection'}
            </Button>
            
            <Button
              type="button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="w-full rounded-full px-6 py-4 h-auto text-base text-[#ADB5BD]"
              data-testid="cancel-reflection-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
